import { displayTimeZonelessDate } from "../helper-functions";
import { Procedure, Tissue, TissueProcedure } from "../models";
import { useTissueQuery } from "../queries";
import { useMemo } from "react";

interface IUseTissueStorage {
    tissue: Tissue | undefined;
    tissueId: number | undefined;
    tissueNumber: string | undefined;
    procedures: Procedure[];
    procedure: Procedure | undefined;
    originalStorageSolution: string | undefined;
    originalLotNumber: string | undefined;
    originalExpDate: string | undefined;
    postCutStorageSolution: string | undefined;
    postCutLotNumber: string | undefined;
    postCutExpDate: string | undefined;
    postCutViewingChamberManufacturerName: string | undefined;
    postCutViewingChamberLotNumber: string | undefined;
    postCutViewingChamberExpDate: string | undefined;
    postCutViewingChamberNa: boolean | undefined;
    latestStorageSolution: string | undefined;
    latestLotNumber: string | undefined;
    latestExpDate: string | undefined;
    isLoading: boolean;
}

export function useTissueStorage(tissueId?: number, procedureId?: number): IUseTissueStorage {
    const {
        detailQuery: { data: tissue, isFetching: isTissueFetching, isLoading: isTissueLoading },
        placementQuery: { data: placement, isFetching: isPlacementFetching, isLoading: isPlacementLoading },
    } = useTissueQuery({ id: tissueId, includeDetails: true });

    const originalStorageSolution =
        placement?.originalMedium === "Other" ? placement?.originalOtherMedium : placement?.originalMedium;
    const originalLotNumber = placement?.originalLotNo;
    const originalExpDate = placement?.originalExpDate;

    const procedures: Procedure[] = useMemo(
        () => (tissue?.tissueProcedures ?? []).map((tp) => tp.procedure).filter(Boolean) as Procedure[],
        [tissue?.tissueProcedures]
    );
    const latestTissueProcedure: TissueProcedure | undefined = (tissue?.tissueProcedures ?? [])
        .filter((tp) => !!tp.procedure?.performedOn)
        .sort(
            (a, b) => new Date(b.procedure?.performedOn!).getTime() - new Date(a.procedure?.performedOn!).getTime()
        )[0];

    const tissueProcedure = tissue?.tissueProcedures?.find((tp) => tp.procedureId === Number(procedureId));
    const procedure = procedures.find((procedure) => procedure.id === Number(procedureId));

    const tissueProcedureForProcessingData = tissueProcedure ?? latestTissueProcedure;
    const postCutStorageSolution = tissueProcedureForProcessingData?.storageSolutionName;
    const postCutLotNumber = tissueProcedureForProcessingData?.storageSolutionLotNumber;
    const postCutExpDate = tissueProcedureForProcessingData?.storageSolutionExpDate;
    const postCutViewingChamberManufacturerName =
        tissueProcedureForProcessingData?.procedure?.viewingChamberManufacturerName;
    const postCutViewingChamberLotNumber =
        tissueProcedureForProcessingData?.procedure?.viewingChamberManufacturerLotNumber;
    const postCutViewingChamberExpDate = tissueProcedureForProcessingData?.procedure?.viewingChamberManufacturerExpDate;
    const postCutViewingChamberNa = tissueProcedureForProcessingData?.procedure?.viewingChamberNa;

    const latestStorageSolution = postCutStorageSolution || originalStorageSolution;
    const latestLotNumber = postCutLotNumber || originalLotNumber;
    const latestExpDate = postCutExpDate || originalExpDate;

    const isLoading = isTissueFetching || isTissueLoading || isPlacementFetching || isPlacementLoading;

    return useMemo(
        () => ({
            tissue,
            tissueId: tissue?.id,
            tissueNumber: tissue?.number,
            originalStorageSolution,
            originalLotNumber,
            originalExpDate: displayTimeZonelessDate(originalExpDate),
            procedures,
            procedure,
            postCutStorageSolution,
            postCutLotNumber,
            postCutExpDate: displayTimeZonelessDate(postCutExpDate),
            postCutViewingChamberManufacturerName,
            postCutViewingChamberLotNumber,
            postCutViewingChamberExpDate: displayTimeZonelessDate(postCutViewingChamberExpDate),
            postCutViewingChamberNa,
            latestStorageSolution,
            latestLotNumber,
            latestExpDate: displayTimeZonelessDate(latestExpDate),
            isLoading,
        }),
        [
            tissue,
            originalStorageSolution,
            originalLotNumber,
            originalExpDate,
            procedures,
            procedure,
            postCutStorageSolution,
            postCutLotNumber,
            postCutExpDate,
            postCutViewingChamberManufacturerName,
            postCutViewingChamberLotNumber,
            postCutViewingChamberExpDate,
            postCutViewingChamberNa,
            latestStorageSolution,
            latestLotNumber,
            latestExpDate,
            isLoading,
        ]
    );
}
